import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Search, MessageCircle, AlertTriangle, User, Clock, 
  ArrowDown, Eye, Filter, Download, RefreshCw, Bug
} from 'lucide-react'
import api from '../../lib/api'

interface Conversation {
  id: string
  user_id: string
  user_email: string
  user_name: string
  conversation_id: string
  created_at: string
  last_query_preview: string
  message_count: number
  metadata: any
}

interface FailedQuery {
  id: string
  user_id: string
  user_email: string
  user_name: string
  conversation_id: string
  created_at: string
  user_query: string
  assistant_response: string
  metadata: any
}

interface SearchLog {
  id: string
  user_id: string
  user_email: string
  user_name: string
  query: string
  results_count: number
  created_at: string
  has_results: boolean
}

export function ChatDebugger() {
  const [activeView, setActiveView] = useState<'conversations' | 'failed' | 'logs'>('failed')
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [failedQueries, setFailedQueries] = useState<FailedQuery[]>([])
  const [searchLogs, setSearchLogs] = useState<SearchLog[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null)
  const [conversationDetails, setConversationDetails] = useState<any>(null)
  const [filters, setFilters] = useState({
    userEmail: '',
    dateFrom: '',
    dateTo: '',
    queryContains: ''
  })


  const loadFailedQueries = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/admin/chat/failed-queries?days_back=7&limit=50')
      setFailedQueries(response.data?.failed_queries || [])
    } catch (error) {
      console.error('Error loading failed queries:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadConversations = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filters.userEmail) params.append('user_email', filters.userEmail)
      if (filters.dateFrom) params.append('date_from', filters.dateFrom)
      if (filters.dateTo) params.append('date_to', filters.dateTo)
      params.append('limit', '100')

      const response = await api.get(`/api/admin/chat/conversations?${params}`)
      setConversations(response.data?.conversations || [])
    } catch (error) {
      console.error('Error loading conversations:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSearchLogs = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filters.userEmail) params.append('user_email', filters.userEmail)
      if (filters.queryContains) params.append('query_contains', filters.queryContains)
      if (filters.dateFrom) params.append('date_from', filters.dateFrom)
      if (filters.dateTo) params.append('date_to', filters.dateTo)
      params.append('limit', '200')

      const response = await api.get(`/api/admin/chat/search-logs?${params}`)
      setSearchLogs(response.data?.search_logs || [])
    } catch (error) {
      console.error('Error loading search logs:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadConversationDetails = async (conversationId: string) => {
    try {
      const response = await api.get(`/api/admin/chat/conversation/${conversationId}`)
      setConversationDetails(response)
      setSelectedConversation(conversationId)
    } catch (error) {
      console.error('Error loading conversation details:', error)
    }
  }

  const getDebugAnalysis = async (conversationId: string) => {
    try {
      const response = await api.get(`/api/admin/debug/query-analysis/${conversationId}`)
      console.log('Debug Analysis:', response)
      // You could open this in a modal or separate view
      alert(`Debug Analysis für Conversation ${conversationId}:\n\n${JSON.stringify(response.data?.debug_hints, null, 2)}`)
    } catch (error) {
      console.error('Error getting debug analysis:', error)
    }
  }

  useEffect(() => {
    if (activeView === 'failed') {
      loadFailedQueries()
    } else if (activeView === 'conversations') {
      loadConversations()
    } else if (activeView === 'logs') {
      loadSearchLogs()
    }
  }, [activeView, filters])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE')
  }

  const views = [
    { id: 'failed', label: 'Fehlgeschlagene Anfragen', icon: AlertTriangle },
    { id: 'conversations', label: 'Alle Gespräche', icon: MessageCircle },
    { id: 'logs', label: 'Such-Logs', icon: Search }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Chat Debugging</h2>
          <p className="text-gray-400">
            Analysiere Chat-Verläufe und identifiziere Probleme bei der Wissensfindung
          </p>
        </div>
        <button
          onClick={() => {
            if (activeView === 'failed') loadFailedQueries()
            else if (activeView === 'conversations') loadConversations()
            else if (activeView === 'logs') loadSearchLogs()
          }}
          className="btn btn-secondary flex items-center space-x-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Aktualisieren</span>
        </button>
      </div>

      {/* View Tabs */}
      <div className="flex space-x-1 p-1 glass rounded-xl">
        {views.map((view) => {
          const Icon = view.icon
          return (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id as any)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-all ${
                activeView === view.id
                  ? 'bg-primary-600 text-white'
                  : 'hover:bg-white/10'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{view.label}</span>
            </button>
          )
        })}
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-xl p-4"
      >
        <div className="flex items-center space-x-2 mb-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-300">Filter</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="email"
            placeholder="Benutzer E-Mail"
            value={filters.userEmail}
            onChange={(e) => setFilters(prev => ({ ...prev, userEmail: e.target.value }))}
            className="input"
          />
          <input
            type="date"
            placeholder="Von Datum"
            value={filters.dateFrom}
            onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            className="input"
          />
          <input
            type="date"
            placeholder="Bis Datum"
            value={filters.dateTo}
            onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
            className="input"
          />
          {activeView === 'logs' && (
            <input
              type="text"
              placeholder="Query enthält..."
              value={filters.queryContains}
              onChange={(e) => setFilters(prev => ({ ...prev, queryContains: e.target.value }))}
              className="input"
            />
          )}
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        key={activeView}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {loading ? (
          <div className="glass rounded-xl p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Lade Daten...</p>
          </div>
        ) : (
          <>
            {/* Failed Queries View */}
            {activeView === 'failed' && (
              <div className="space-y-4">
                <div className="text-sm text-gray-400 mb-4">
                  {failedQueries.length} fehlgeschlagene Anfragen in den letzten 7 Tagen
                </div>
                {failedQueries.map((query) => (
                  <motion.div
                    key={query.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="glass rounded-xl p-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-red-500/20 rounded-lg">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                        </div>
                        <div>
                          <div className="font-medium text-white">{query.user_name}</div>
                          <div className="text-sm text-gray-400">{query.user_email}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => loadConversationDetails(query.conversation_id)}
                          className="btn btn-sm btn-secondary flex items-center space-x-1"
                        >
                          <Eye className="w-3 h-3" />
                          <span>Details</span>
                        </button>
                        <button
                          onClick={() => getDebugAnalysis(query.conversation_id)}
                          className="btn btn-sm btn-primary flex items-center space-x-1"
                        >
                          <Bug className="w-3 h-3" />
                          <span>Debug</span>
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Benutzer-Frage:</span>
                        <p className="text-sm text-gray-300 mt-1">{query.user_query}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">System-Antwort:</span>
                        <p className="text-sm text-gray-400 mt-1">{query.assistant_response}</p>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{formatDate(query.created_at)}</span>
                        <span>ID: {query.conversation_id}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Conversations View */}
            {activeView === 'conversations' && (
              <div className="space-y-4">
                <div className="text-sm text-gray-400 mb-4">
                  {conversations.length} Gespräche gefunden
                </div>
                {conversations.map((conv) => (
                  <motion.div
                    key={conv.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="glass rounded-xl p-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg">
                          <User className="w-4 h-4 text-blue-400" />
                        </div>
                        <div>
                          <div className="font-medium text-white">{conv.user_name}</div>
                          <div className="text-sm text-gray-400">{conv.user_email}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => loadConversationDetails(conv.conversation_id)}
                        className="btn btn-sm btn-secondary flex items-center space-x-1"
                      >
                        <Eye className="w-3 h-3" />
                        <span>Öffnen</span>
                      </button>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Letzte Nachricht:</span>
                        <p className="text-sm text-gray-300 mt-1">{conv.last_query_preview}</p>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{formatDate(conv.created_at)}</span>
                        <span>{conv.message_count} Nachrichten</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Search Logs View */}
            {activeView === 'logs' && (
              <div className="space-y-4">
                <div className="text-sm text-gray-400 mb-4">
                  {searchLogs.length} Such-Einträge gefunden
                  {searchLogs.length > 0 && (
                    <span className="ml-4">
                      ({searchLogs.filter(log => !log.has_results).length} fehlgeschlagen)
                    </span>
                  )}
                </div>
                {searchLogs.map((log) => (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`glass rounded-xl p-4 hover:bg-white/5 transition-colors border-l-4 ${
                      log.has_results ? 'border-green-500' : 'border-red-500'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          log.has_results ? 'bg-green-500/20' : 'bg-red-500/20'
                        }`}>
                          <Search className={`w-4 h-4 ${
                            log.has_results ? 'text-green-400' : 'text-red-400'
                          }`} />
                        </div>
                        <div>
                          <div className="font-medium text-white">{log.user_name}</div>
                          <div className="text-sm text-gray-400">{log.user_email}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${
                          log.has_results ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {log.results_count} Ergebnisse
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(log.created_at)}
                        </div>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Suchanfrage:</span>
                      <p className="text-sm text-gray-300 mt-1">{log.query}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* Conversation Details Modal */}
      {selectedConversation && conversationDetails && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedConversation(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="glass rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-white">Gesprächsdetails</h3>
                <p className="text-gray-400">
                  {conversationDetails.user.name} ({conversationDetails.user.email})
                </p>
              </div>
              <button
                onClick={() => setSelectedConversation(null)}
                className="btn btn-secondary"
              >
                Schließen
              </button>
            </div>

            <div className="space-y-4">
              {conversationDetails.history.map((interaction: any, index: number) => (
                <div key={interaction.id} className="border-l-2 border-gray-600 pl-4">
                  <div className="text-xs text-gray-500 mb-2">
                    {formatDate(interaction.created_at)} - Interaktion {index + 1}
                  </div>
                  {interaction.messages.map((message: any, msgIndex: number) => (
                    <div
                      key={msgIndex}
                      className={`mb-3 p-3 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-blue-500/20 border-blue-500/30'
                          : 'bg-gray-500/20 border-gray-500/30'
                      } border`}
                    >
                      <div className="text-xs font-medium text-gray-400 mb-1">
                        {message.role === 'user' ? 'Benutzer' : 'Assistent'}
                      </div>
                      <div className="text-sm text-gray-200">
                        {message.content}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}